import { generateSlugFromName, sanitizeSlug } from '@saaga/shared/utils/slug.util'
import { WandSparklesIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import type { ControllerRenderProps, UseFormReturn } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

import type { AgencyFormSchemaType } from '../agency-form.schema'

interface SlugInputProps {
  field: ControllerRenderProps<AgencyFormSchemaType, 'slug'>
  isDisabled: boolean
  onGenerate: () => void
  onSlugChange: (value: string) => void
}

const SlugInput = ({ field, isDisabled, onGenerate, onSlugChange }: SlugInputProps): ReactNode => (
  <div className="flex gap-2">
    <Input
      placeholder="my-agency-slug"
      disabled={isDisabled}
      className="agency-form-slug-input"
      {...field}
      onChange={(e) => onSlugChange(e.target.value)}
    />
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={isDisabled}
          onClick={onGenerate}
          className="agency-form-slug-generate shrink-0"
        >
          <WandSparklesIcon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Generate from name</TooltipContent>
    </Tooltip>
  </div>
)

interface AgencyFormSlugFieldProps {
  form: UseFormReturn<AgencyFormSchemaType>
  isDisabled?: boolean
}

export const AgencyFormSlugField = ({
  form,
  isDisabled = false,
}: AgencyFormSlugFieldProps): ReactNode => {
  const handleGenerateSlug = (): void => {
    const name = form.getValues('name')
    if (name) {
      form.setValue('slug', generateSlugFromName(name), { shouldValidate: true, shouldDirty: true })
    }
  }

  const handleSlugChange = (value: string): void => {
    form.setValue('slug', sanitizeSlug(value), { shouldValidate: true, shouldDirty: true })
  }

  return (
    <FormField
      control={form.control}
      name="slug"
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            Slug <span className="text-destructive">*</span>
          </FormLabel>
          <FormControl>
            <SlugInput
              field={field}
              isDisabled={isDisabled}
              onGenerate={handleGenerateSlug}
              onSlugChange={handleSlugChange}
            />
          </FormControl>
          <FormDescription>URL-friendly identifier (lowercase, numbers, hyphens)</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

AgencyFormSlugField.displayName = 'AgencyFormSlugField'
